/**
 * Adds multiple values (string or number) with special handling for comma decimals and percentages.
 * - If any value is a percentage string (e.g. "12,5%"), all are treated as percentages and the result is returned as a string with %.
 * - If any value is a string with a comma decimal, it is converted to number.
 * - Any other string is treated as 0.
 */
export function addSmart(...values: Array<string | number>): number | string {
  // Helper to parse a value to number, handling comma decimals and percentages
  const parseValue = (val: string | number): { num: number, isPercent: boolean } => {
    if (typeof val === 'number') return { num: val, isPercent: false };
    if (typeof val === 'string') {
      let str = val.trim();
      if (str.endsWith('%')) {
        str = str.slice(0, -1).replace(',', '.');
        const num = parseFloat(str);
        return { num: isNaN(num) ? 0 : num, isPercent: true };
      }
      // comma decimal
      if (/^-?\d+(,\d+)?$/.test(str)) {
        const num = parseFloat(str.replace(',', '.'));
        return { num: isNaN(num) ? 0 : num, isPercent: false };
      }
      // plain number
      const num = parseFloat(str);
      return { num: isNaN(num) ? 0 : num, isPercent: false };
    }
    return { num: 0, isPercent: false };
  };

  let isPercent = false;
  let sum = 0;

  for (const val of values) {
    const parsed = parseValue(val);
    if (parsed.isPercent) isPercent = true;
    sum += parsed.num;
  }

  return isPercent ? `${sum}%` : sum;
}
